import * as ToastPrimitive from "@radix-ui/react-toast";
import { Check, X, AlertTriangle } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Toasts, kept deliberately small: this console confirms writes inline wherever it can, and a
 * toast is for the case where the thing that changed is no longer on screen (a deleted row, a
 * rotated password). Radix handles the swipe-to-dismiss and the screen-reader live region.
 */

type Tone = "success" | "error" | "info";
interface ToastMessage {
  id: number;
  title: string;
  description?: string;
  tone: Tone;
}

interface ToastApi {
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = React.createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = React.useState<ToastMessage[]>([]);
  const nextId = React.useRef(0);

  const push = React.useCallback((tone: Tone, title: string, description?: string) => {
    setMessages((prev) => [...prev, { id: nextId.current++, title, description, tone }]);
  }, []);

  const api = React.useMemo<ToastApi>(
    () => ({
      success: (t, d) => push("success", t, d),
      error: (t, d) => push("error", t, d),
      info: (t, d) => push("info", t, d),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
        {children}
        {messages.map((m) => (
          <ToastPrimitive.Root
            key={m.id}
            onOpenChange={(open) =>
              !open && setMessages((prev) => prev.filter((x) => x.id !== m.id))
            }
            className={cn(
              "flex items-start gap-2.5 rounded-lg border bg-surface-raised p-3 shadow-lg",
              "data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-80",
              "data-[swipe=end]:animate-out data-[swipe=end]:fade-out-0",
              m.tone === "error" ? "border-danger-4" : "border-border",
            )}
          >
            <span
              className={cn(
                "mt-px flex size-4 shrink-0 items-center justify-center rounded-full",
                m.tone === "success" && "bg-success-9 text-white",
                m.tone === "error" && "bg-danger-9 text-white",
                m.tone === "info" && "bg-accent-9 text-white",
              )}
              aria-hidden
            >
              {m.tone === "error" ? (
                <AlertTriangle className="size-2.5" />
              ) : (
                <Check className="size-2.5" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <ToastPrimitive.Title className="text-sm font-medium text-text">
                {m.title}
              </ToastPrimitive.Title>
              {m.description && (
                <ToastPrimitive.Description className="mt-0.5 text-xs text-text-muted">
                  {m.description}
                </ToastPrimitive.Description>
              )}
            </div>
            <ToastPrimitive.Close className="rounded p-0.5 text-text-subtle hover:bg-surface-hover hover:text-text">
              <X className="size-3.5" />
              <span className="sr-only">Dismiss</span>
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport
          className={cn(
            "fixed bottom-0 right-0 z-100 flex max-h-screen w-full flex-col-reverse gap-2",
            "p-4 sm:max-w-sm",
          )}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
